import { Button, Stack, Typography } from "@mui/material";
import { useVerificationsQuery } from "Api/Queries/Verifications/useVerificationsQuery";
import { Colors } from "Components/Layout/Themes/Colors";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { useFormatLocale } from "Hooks/useFormatLocale";
import { isValid } from "date-fns";
import { useNavigate } from "Navigation/useNavigate";
import { Resources, useResource, type TFunction } from "Translations/Resources";
import { EXPIRATION_WARNING_DAYS } from "Utils/DocumentExpiration/documentExpirationUtils";
import { getDaysUntilDate } from "Utils/DateUtils";
import { appUrl } from "Utils/UrlUtils";

const COMPONENT_RESOURCES = Resources.Components.DocumentExpirationWarning;

type DocumentWarning = {
  isExpired: boolean;
  title: string;
  message: string;
};

const getDocumentWarning = (
  dateString: string | null | undefined,
  isStatusExpired: boolean,
  format: (date: Date) => string,
  t: TFunction,
  expiredTitle: typeof COMPONENT_RESOURCES.ExpiredPrimaryTitle,
  expiredMessage: typeof COMPONENT_RESOURCES.ExpiredPrimaryMessage,
  warningTitle: typeof COMPONENT_RESOURCES.WarningPrimaryTitle,
  warningMessage: typeof COMPONENT_RESOURCES.WarningPrimaryMessage,
): DocumentWarning | null => {
  const date = dateString ? new Date(dateString) : null;
  const days = getDaysUntilDate(date);
  const isExpired = (date !== null && days <= 0) || isStatusExpired;
  const isExpiring =
    date !== null && days > 0 && days <= EXPIRATION_WARNING_DAYS;

  if (isExpired) {
    return {
      isExpired: true,
      title: t(expiredTitle),
      message: t(expiredMessage),
    };
  }

  if (isExpiring) {
    const formattedDate = date && isValid(date) ? format(date) : "";
    return {
      isExpired: false,
      title: t(warningTitle),
      message: t(warningMessage, { date: formattedDate }),
    };
  }

  return null;
};

export const DocumentExpirationWarningPopup = () => {
  const { t } = useResource();
  const { data, isLoading } = useVerificationsQuery();
  const format = useFormatLocale();
  const navigate = useNavigate();

  if (isLoading || !data) {
    return null;
  }

  const isStatusExpired = data.identityVerificationStatus === "Expired";

  const warnings = [
    getDocumentWarning(
      data.documentExpirationDate,
      isStatusExpired,
      format,
      t,
      COMPONENT_RESOURCES.ExpiredPrimaryTitle,
      COMPONENT_RESOURCES.ExpiredPrimaryMessage,
      COMPONENT_RESOURCES.WarningPrimaryTitle,
      COMPONENT_RESOURCES.WarningPrimaryMessage,
    ),
    getDocumentWarning(
      data.secondDocumentExpirationDate,
      false,
      format,
      t,
      COMPONENT_RESOURCES.ExpiredSecondaryTitle,
      COMPONENT_RESOURCES.ExpiredSecondaryMessage,
      COMPONENT_RESOURCES.WarningSecondaryTitle,
      COMPONENT_RESOURCES.WarningSecondaryMessage,
    ),
  ].filter((w): w is DocumentWarning => w !== null);

  if (warnings.length === 0) {
    return null;
  }

  const hasAnyExpired = warnings.some((w) => w.isExpired);

  return (
    <PrimaryPaper>
      <Stack gap={2}>
        {warnings.map((warning) => (
          <Stack key={warning.title}>
            <Typography
              color={warning.isExpired ? Colors.Red : Colors.Warning}
              align="left"
              fontWeight={600}
              marginBottom={1}
            >
              {warning.title}
            </Typography>
            <Typography
              align="left"
              color={warning.isExpired ? Colors.Red : Colors.Warning}
            >
              {warning.message}
            </Typography>
          </Stack>
        ))}
        <Button
          variant="outlined"
          color={hasAnyExpired ? "error" : "warning"}
          onClick={() => {
            navigate(appUrl("user-account"));
          }}
        >
          {t(COMPONENT_RESOURCES.UploadNewDocument)}
        </Button>
      </Stack>
    </PrimaryPaper>
  );
};
